import React, { useEffect } from 'react';

import { type History } from 'history';
import { Route, Router, Switch } from 'react-router-dom';

// Root
import { useTeamVisibility } from '@atlassian/ptc-common';

import AgentProfile from './AgentProfile';
import KudosPage from './KudosPage';
import MyProfileRedirect from './MyProfileRedirect';
import RootRedirect from './RootRedirect';
// Search
import { RouteWithOrgIdLoading } from './RouteWithOrgIdLoading';
import SearchPage from './SearchPage';
// User Profile
import SearchTeamsError from './SearchTeamsError';
import TeamProfile from './TeamProfile';
import TeamProfileNoId from './TeamProfileNoId';
import UserProfile from './UserProfile';
import UserProfileUnknownRoute from './UserProfileUnknownRoute';
import UserProfileWorkedOn from './UserProfileWorkedOn';

// Team Profile

// Kudos

const renderRoutes = (contextEntryPoint: string, canViewTeams: boolean) => {
	return (
		<Switch>
			<Route exact path={`${contextEntryPoint}/`} render={RootRedirect} />
			{!canViewTeams && (
				<Route path={`${contextEntryPoint}/search/teams`} render={SearchTeamsError} />
			)}
			<Route path={`${contextEntryPoint}/search`} render={SearchPage} />
			<Route path={`${contextEntryPoint}/kudos/:uuid`} render={KudosPage} />
			<Route exact path={`${contextEntryPoint}/team`} render={TeamProfileNoId} />
			<RouteWithOrgIdLoading path={`${contextEntryPoint}/team/:teamId`} render={TeamProfile} />
			<Route path={`${contextEntryPoint}/agent/:agentId`} render={AgentProfile} />

			<Route path={`${contextEntryPoint}/:userId/work`} render={UserProfileWorkedOn} />
			<Route path={`${contextEntryPoint}/:userId/*`} render={UserProfileUnknownRoute} />
			<Route
				path={`${contextEntryPoint}/me`}
				render={(props) => <MyProfileRedirect {...props} contextEntryPoint={contextEntryPoint} />}
			/>
			<Route path={`${contextEntryPoint}/:userId`} render={UserProfile} />
		</Switch>
	);
};

interface Props {
	history?: History;
	contextEntryPoint: string;
	onTransition?: (newPath: string) => void;
}

function AppRoutes(props: Props) {
	const { contextEntryPoint, history, onTransition } = props;
	const {
		permissions: { canViewTeams },
	} = useTeamVisibility();

	useEffect(() => {
		if (!onTransition) {
			return;
		}

		let unsubscribeBlockingHistory = () => {};
		let unsubscribeListeningHistory = () => {};

		if (history) {
			let lastHistoryLocation: string;
			unsubscribeListeningHistory = history.listen(({ pathname }) => {
				lastHistoryLocation = pathname;
			});

			// We're using history.block() here instead of history.listen()
			// because we want this to run BEFORE transition happens.
			unsubscribeBlockingHistory = history.block(({ pathname }) => {
				if (lastHistoryLocation !== pathname) {
					onTransition(pathname);
				}
			});
		}

		return () => {
			unsubscribeBlockingHistory();
			unsubscribeListeningHistory();
		};
	}, [onTransition, history]);

	const routes = renderRoutes(contextEntryPoint, canViewTeams);

	// If `history` object is not passed to `EmbeddableDirectory`, `EmbeddableDirectory` will use `Router` component from product in higher component tree.
	// If `history` object is passed to `EmbeddableDirectory`, `EmbeddableDirectory` will render its own `Router` component.
	// @ts-ignore - Property 'children' does not exist on type 'IntrinsicAttributes & IntrinsicClassAttributes<Router> & Readonly<RouterProps>'
	// This was added when enrolling `@atlassian/ptc-embeddable-directory` into JFE local consumption
	return history ? <Router history={history}>{routes}</Router> : routes;
}

export default AppRoutes;
